/**
 * Hook for generating the authoritative CoCM billing-code packet.
 *
 * Mirrors `useGenerateBillingSummary`:
 * - Development: Vite proxy adds the API key server-side
 * - Staging/Production: Direct API Gateway call with API key
 *
 * Unlike the summary hook (which returns an AI narrative), this returns the
 * versioned `BillingCodePacket` from the authoritative engine — the codes the
 * stepped modal previews and freezes at submit.
 *
 * @agentic connections
 */

import { useCallback, useState } from "react";
import { useMedplum } from "@medplum/react";

import { ensureActiveSession } from "@/utils/auth/ensureActiveSession";
import { getApiEndpoint, getApiAuthHeaders } from "@/utils/api/getApiEndpoint";
import type { BillingCodePacket } from "@benchmark/core/types";

/** Result from {@link useGenerateBillingCodes}. @agentic connections */
export type UseGenerateBillingCodesResult = {
  generateCodes: (
    patientId: string,
    billingMonth: string,
  ) => Promise<BillingCodePacket | null>;
  isGenerating: boolean;
  error: Error | null;
};

/**
 * Hook to generate the authoritative billing-code packet via the API.
 * @agentic connections
 */
export const useGenerateBillingCodes = (): UseGenerateBillingCodesResult => {
  const medplum = useMedplum();
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const generateCodes = useCallback(
    async (
      patientId: string,
      billingMonth: string,
    ): Promise<BillingCodePacket | null> => {
      if (!patientId || !billingMonth) {
        const err = new Error("Patient ID and billing month are required");
        setError(err);
        return null;
      }

      setIsGenerating(true);
      setError(null);

      try {
        const accessToken = await ensureActiveSession(medplum);
        const apiUrl = getApiEndpoint("/api/v1/billing-codes");

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 60000);

        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Medplum-Authorization": `Bearer ${accessToken}`,
            ...getApiAuthHeaders(),
          },
          body: JSON.stringify({ patientId, billingMonth }),
          signal: controller.signal,
        }).finally(() => clearTimeout(timeout));

        if (!response.ok) {
          const errorText = await response.text().catch(() => "");
          let errorData: Record<string, unknown> = {};
          try {
            errorData = JSON.parse(errorText);
          } catch {
            errorData = { raw: errorText };
          }
          const errorMessage =
            (typeof errorData.error === "string" ? errorData.error : null) ||
            (typeof errorData.message === "string"
              ? errorData.message
              : null) ||
            `Failed to generate codes: ${response.status} ${response.statusText}`;
          throw new Error(errorMessage);
        }

        const data: BillingCodePacket = await response.json();
        setIsGenerating(false);
        return data;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to generate codes. Please try again.";
        const errorObj = new Error(errorMessage);
        setError(errorObj);
        setIsGenerating(false);
        return null;
      }
    },
    [medplum],
  );

  return { generateCodes, isGenerating, error };
};
